import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
} from "amazon-cognito-identity-js";
import { APP_CONFIG } from "config/appConfig";
import { HttpStatus } from "constant";
import { Login } from "generated/models";
import {
    AuthMFAResponse,
    AuthResponse,
    IAuthenticationStrategy,
    MFASubmissionParams,
} from "../authenticationTypes";

export class CognitoAuth {
    private readonly userPool: CognitoUserPool;

    constructor(
        private readonly userPoolId: string,
        private readonly clientId: string
    ) {
        this.userPool = new CognitoUserPool({
            UserPoolId: this.userPoolId,
            ClientId: this.clientId,
        });
    }

    async authenticate(
        username: string,
        password: string
    ): Promise<AuthResponse> {
        const userData = {
            Username: username,
            Pool: this.userPool,
        };

        const authDetails = new AuthenticationDetails({
            Username: username,
            Password: password,
        });

        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve) => {
            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (session) => {
                    resolve({
                        status: HttpStatus.OK,
                        data: {
                            AccessToken: session.getAccessToken().getJwtToken(),
                            IdToken: session.getIdToken().getJwtToken(),
                            RefreshToken: session.getRefreshToken().getToken(),
                            LastLogin: session.getAccessToken().getExpiration(),
                        },
                    });
                },
                onFailure: (err) => {
                    resolve({ status: HttpStatus.BAD_REQUEST, error: err });
                },
                mfaRequired: (challengeName, challengeParameters) => {
                    resolve({
                        status: HttpStatus.OK,
                        data: {
                            ChallengeName: challengeName,
                            Session: cognitoUser,
                            ChallengeParameters: challengeParameters,
                        } as AuthMFAResponse,
                    });
                },
            });
        });
    }

    async sendMfaCode(
        cognitoUser: CognitoUser,
        mfaCode: string
    ): Promise<AuthResponse> {
        return new Promise((resolve) => {
            cognitoUser.sendMFACode(mfaCode, {
                onSuccess: (session: CognitoUserSession) => {
                    resolve({
                        status: HttpStatus.OK,
                        data: {
                            AccessToken: session.getAccessToken().getJwtToken(),
                            IdToken: session.getIdToken().getJwtToken(),
                            RefreshToken: session.getRefreshToken().getToken(),
                            LastLogin: session.getAccessToken().getExpiration(),
                        },
                    });
                },
                onFailure: (err) => {
                    resolve({ status: HttpStatus.BAD_REQUEST, error: err });
                },
            });
        });
    }
}

export class CognitoAuthStrategy implements IAuthenticationStrategy {
    private readonly cognitoAuth: CognitoAuth;

    constructor() {
        const userPoolId = APP_CONFIG.APP_AWS_POOL_ID;
        const clientId = APP_CONFIG.APP_AWS_CLIENT_ID;

        if (!userPoolId || !clientId) {
            throw new Error("Both UserPoolId and ClientId are required.");
        }

        this.cognitoAuth = new CognitoAuth(userPoolId, clientId);
    }

    async login({ email, password }: Login): Promise<AuthResponse> {
        return await this.cognitoAuth.authenticate(email, password);
    }

    async submitMFACode({
        session,
        mfaCode,
    }: MFASubmissionParams): Promise<AuthResponse> {
        if (!(session instanceof CognitoUser)) {
            throw new Error("Invalid session type for Cognito authentication");
        }
        return await this.cognitoAuth.sendMfaCode(session, mfaCode);
    }
}
